import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import UserName from '../common/user/UserName';
import { useDebounce } from '../../utils/useDebounce';
import { colourStyles } from '../locations/Locations';
import SearchIcon from '../../images/icon-search.svg';
import styles from './UserManagement.module.css'
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import { useDispatch } from 'react-redux';
import { activateUser, deactivateUser, getUserDetails, getUserManagementList } from '../../redux/user-management/actions/userManagement.actions';
import { useSelector } from 'react-redux';
import Loader from '../common/loader/Loader';
import PhoneInput from 'react-phone-input-2';
import Pagination from '../common/pagination/pagination';
import { useNavigate } from 'react-router-dom';
import MageDotsIcon from '../svgIcons/MageDotsIcon';
import Toast from '../common/toast/Toast';
import { hasPermission } from '../auth/login/Login';

const FilterOptions = [
    { label: 'Show All', value: 'both' },
    { label: 'Deactivate request', value: 'deactivate' },
    { label: 'Activate Request', value: 'activate' }
];

const pageLimit = 10;

interface UserListProps {
    id: string;
    name: string;
    email: string;
    contact: number | string;
    requestType: 'Delete' | 'Deactivate' | 'Activate' | '',
    countryCode: string | number | null;
    actionDate: string | null;
}

const UserManagement = () => {
    const navigate = useNavigate();
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const [searchText, setSearchText] = useState('');
    const [selectedFilter, setSelectedFilter] = useState(FilterOptions[0]);
    const dispatch: AppDispatch = useDispatch();
    const [userList, setUserList] = useState<null | any>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const searchWithDebounce = useDebounce(searchText, 1000, setPageNumber);
    const [totalCount, setTotalCount] = useState(0);
    const [showActionBox, setShowActionBox] = useState('');
    const [actionBoxPosition, setActionBoxPosition] = useState<'top' | 'bottom'>('bottom');
    const actionButtonRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [toastType, setToastType] = useState('error');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const canDelete = hasPermission("User Management", "Users Management", "delete");

    const handleActionButtonClick = (userId: string) => {
        const rowRef = actionButtonRefs.current[userId];
        if (rowRef) {
            const rect = rowRef.getBoundingClientRect();

            const parentRect = rowRef.closest(`.${styles.listingWrapper}`)?.getBoundingClientRect();

            if (parentRect) {
                const parentHeight = parentRect.height;
                const parentBottom = parentRect.bottom;

                if (rect.bottom > parentBottom - 100) {
                    setActionBoxPosition('top');
                } else {
                    setActionBoxPosition('bottom');
                }

                setShowActionBox(showActionBox !== userId ? userId : '');
            }
            // const viewportHeight = window.innerHeight;

            // if (rect.bottom > viewportHeight - 100) {
            //     setActionBoxPosition('top');
            // } else {
            //     setActionBoxPosition('bottom');
            // }
            // setShowActionBox(showActionBox !== userId ? userId : '');
        }
    };

    const { data: userListData, errorMsg: userListError, loading: userListLoading, count: userCount } = useSelector((state: IRootState) => state.managementReducer);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const trimmedValue = value.replace(/^\s+/, '');

        setSearchText(trimmedValue);
    };

    const userManagementHeader = [
        { title: 'Name' },
        { title: 'Email' },
        { title: 'Contact No' },
        { title: 'Request Type' },
        { title: '' }
    ];

    useEffect(() => {
        canDelete && dispatch(getUserManagementList(pageNumber, searchWithDebounce, selectedFilter.value, pageLimit))
    }, [searchWithDebounce, pageNumber, selectedFilter]);

    useEffect(() => {
        if (userListData?.length && !userListLoading) {
            const newUserList: UserListProps[] = [];
            userListData?.length > 0 && userListData?.forEach((user: any) => {
                if (user?.activateRequestDate || user?.deActivateRequestDate || user?.deleteAccountRequestDate) {
                    let requestType: 'Delete' | 'Deactivate' | 'Activate' | '' = '';
                    let actionDate: string | null = null;

                    if (user.activateRequestDate && user.activateDate === null) {
                        requestType = 'Activate';
                        actionDate = user?.activateRequestDate
                    } else if (user.deActivateRequestDate && user.deActivateDate === null) {
                        requestType = 'Deactivate';
                        actionDate = user?.deActivateRequestDate
                    } else if (user.deleteAccountRequestDate) {
                        requestType = 'Delete';
                    }

                    newUserList.push({
                        id: user?.id,
                        name: user.firstName + ' ' + user.lastName,
                        contact: user.phoneNumber,
                        email: user.email,
                        requestType: requestType,
                        countryCode: user?.countryCode || '+1',
                        actionDate: actionDate
                    })
                }
            });
            setUserList(newUserList);
            setTotalCount(userCount || 0)
        } else if (userListData?.length === 0 && !userListLoading) {
            setUserList(null);
            setPageNumber(1);
            setTotalCount(0);
        }
    }, [userListData, userListLoading, userCount]);

    const onActivateSuccess = async (message: string) => {
        setToastMessage(message);
        setShowToast(true);
        setToastType('success')
        userInfo && userInfo.role === 'super_admin' && await dispatch(getUserManagementList(pageNumber, searchWithDebounce, selectedFilter.value, pageLimit))
        setIsLoading(false);
    }

    const onActivateFailure = (message: string) => {
        setIsLoading(false);
        setToastMessage(message);
        setShowToast(true);
        setToastType('error')
    }

    const onActivate = async (id: string) => {
        if (id) {
            setIsLoading(true)
            dispatch(activateUser(id, onActivateSuccess, onActivateFailure))
        }
    }

    const onDeactivateSuccess = async (message: string) => {
        setToastMessage(message);
        setShowToast(true);
        setToastType('success')
        userInfo && userInfo.role === 'super_admin' && await dispatch(getUserManagementList(pageNumber, searchWithDebounce, selectedFilter.value, pageLimit))
        setIsLoading(false);
    }

    const onDeactivateFailure = (message: string) => {
        setIsLoading(false);
        setToastMessage(message);
        setShowToast(true);
        setToastType('error')
    }

    const onDeactivate = (id: string) => {
        if (id) {
            setIsLoading(true)
            dispatch(deactivateUser(id, onDeactivateSuccess, onDeactivateFailure))
        }
    }

    return (
        <div className={styles.pageContainer}>

            {userListLoading && <Loader />}

            <div className={styles.pageHeader}>
                <div className={styles.userGreet}>
                    <p>
                        Welcome <UserName />
                    </p>
                    <h2>User Management</h2>
                </div>

                {canDelete && <div className={styles.searchAndFilter}>
                    <div className={styles.filterDropDown}>
                        <Select
                            onChange={(selectedOption) => {
                                if (selectedOption?.value) {
                                    setSelectedFilter(selectedOption);
                                }
                            }}
                            isSearchable={false}
                            classNamePrefix="custom-react-select"
                            value={
                                selectedFilter.value !== ''
                                    ? { value: selectedFilter.value, label: selectedFilter.label }
                                    : undefined
                            }
                            options={FilterOptions}
                            placeholder="Select Option"
                            styles={colourStyles}
                        />
                    </div>

                    <div className={styles.search}>
                        <input
                            onChange={(e) => handleChange(e)}
                            type="text"
                            placeholder="Search"
                            maxLength={50}
                            value={searchText}
                        />
                        <img className={styles.searchIconImage} src={SearchIcon} alt="search-icon" />
                    </div>
                </div>}
            </div>

            {canDelete && <div className={styles.bottomContainer}>
                <div className={`${styles.listingWrapper} customScrollbar `}>
                    <table className='visitorListing'>
                        <thead>
                            <tr>
                                {userManagementHeader?.map((header: { title: string }, index: number) => {
                                    return <th key={index}>{header.title}</th>
                                })}
                            </tr>
                        </thead>

                        <tbody>
                            {!userListLoading && !!userList?.length && userList?.map((user: UserListProps) => {

                                return <tr
                                    ref={(ref) => (actionButtonRefs.current[user?.id] = ref)}
                                    key={user.id} onClick={() => navigate(`/user-management/${user?.id}`)}>
                                    <td>{user.name}</td>
                                    <td className={styles.email}>{user.email}</td>
                                    <td className="phone-col">
                                        <PhoneInput value={String(`+${user?.countryCode || '+1'}${user.contact || ''}`)} onClick={() => navigate(`/user-management/${user?.id}`)} />
                                    </td>
                                    <td>{user?.requestType}</td>
                                    <td>
                                        {!!showActionBox && <div className={styles.dropDownBackDrop} onClick={() => setShowActionBox('')}></div>}
                                        {user?.actionDate !== null && <div className={styles.actionButtonWrapper}>
                                            <button className={styles.MageDotsIcon}
                                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                    event.stopPropagation();
                                                    handleActionButtonClick(user?.id)
                                                }}
                                            >
                                                <MageDotsIcon />
                                            </button>
                                            <div className={`${styles.actionContainer} ${user?.id === showActionBox ? styles.active : ''} ${actionBoxPosition === 'top' ? styles.top : ''}`}>
                                                <div className={styles.actionBox} role='button' onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                    event.stopPropagation();
                                                    user?.requestType === 'Activate' ? onActivate(user.id) : onDeactivate(user.id)
                                                }}>
                                                    <span>{user?.requestType}</span>
                                                </div>
                                            </div>
                                        </div>}
                                    </td>
                                </tr>
                            })}

                            {(!userList?.length && !userListLoading) &&
                                <tr >
                                    <td colSpan={5} className={styles.noData}>
                                        No Data Found
                                    </td>
                                </tr>}
                        </tbody>
                    </table>
                </div>

                <div>
                    <Pagination
                        className="pagination-bar"
                        currentPage={pageNumber}
                        totalCount={Math.ceil(totalCount / pageLimit)}
                        pageSize={1}
                        onPageChange={(page: number) => {
                            setPageNumber(page);
                        }}
                    />
                </div>
            </div>}
            {showToast && <Toast type={toastType} message={toastMessage || ''} />}
        </div>
    )
}

export default UserManagement
